import React from 'react'
import { useNavigate } from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { get } from 'client/utils/api-routes.common'
import {
  DcBreadcrumbs,
  DcDiv,
  DcFlexBox,
  DcText,
  PixelFactoryContext,
  PixelPageContainer,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'
import { IPixelFactory } from '../../../../../decl'
import MarketplaceStallIcon from '../../../../utils/icons/marketplace-stall-icon'
import { WarehouseIcon } from '../../../../utils/icons/warehouse-icon'
const WarehouseView = () => {
  const { showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  interface WarehouseZone {
    id: string
    zone_name: string
  }

  interface CreatedAt {
    date: string
    timezone_type: number
    timezone: string
  }

  interface IWarehouse {
    wh_id: number
    id: number
    name: string
    warehouse_code: string
    is_default: boolean
    address: string
    description: string
    city: string
    zip: string | null
    province: string | null
    country: string | null
    phone: string
    active: boolean
    created_at: CreatedAt
    zones: WarehouseZone[]
  }

  interface WarehousesResponse {
    data: IWarehouse[]
  }

  const [warehouses, setWarehouses] = React.useState<IWarehouse[]>([])
  const [marketplaces, setMarketplaces] = React.useState<IWarehouse[]>([])
  const navigate = useNavigate()

  const getAllWarehouses = () => {
    showPageLoader()
    get('/warehouses').subscribe((res) => {
      const response = res.response as WarehousesResponse
      setWarehouses(response?.data)
      hidePageLoader()
    })
  }

  const getAllMarketplaces = () => {
    showPageLoader()
    get('/market-places').subscribe((res) => {
      const response = res.response as WarehousesResponse
      setMarketplaces(response?.data)
      hidePageLoader()
    })
  }

  React.useEffect(() => {
    getAllWarehouses()
    getAllMarketplaces()
  }, [])

  const breadcrumbs = [
    {
      name: 'Warehouses & Marketplaces',
      active: true,
    },
  ]

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const handleWarehouseClick = (warehouse_id: number, type: string) => {
    if (type === 'warehouse') {
      navigate(
        `/inventory/warehouse-wise?warehouse_id=${warehouse_id}&type=warehouse`,
      )
    } else {
      navigate(
        `/inventory/warehouse-wise?marketplace_id=${warehouse_id}&type=marketplace`,
      )
    }
  }

  return (
    <>
      <SectionContent
        height="60px"
        fontSize="16px"
        style={{
          fontSize: '16px',
          padding: '8px 20px',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
          borderRadius: '0px',
        }}
      >
        <DcFlexBox justifyContent="space-between" alignItems="center">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <ProfileSection />
        </DcFlexBox>
      </SectionContent>

      <PixelPageContainer
        height="calc(100vh - 65px)"
        style={{ padding: '20px' }}
      >
        <Section>
          <SectionContent height="50%" overflow="auto">
            <DcFlexBox height="auto" flexDirection="column" gap="20px">
              <DcText style={{ fontSize: '18px', fontWeight: 'bold' }}>
                <WarehouseIcon
                  color={colors.grayTextDark}
                  marginBottom="5px"
                  size={20}
                />{' '}
                Warehouse list
              </DcText>
              <DcDiv style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {warehouses.map((warehouse) => (
                  <DcDiv
                    key={warehouse.id}
                    hoverStyle={
                      !warehouse.is_default
                        ? { boxShadow: `0px 0px 10px 0px ${colors.grayShadow}` }
                        : { boxShadow: `0px 0px 10px 0px ${colors.green}` }
                    }
                    onClick={() =>
                      handleWarehouseClick(warehouse.wh_id, 'warehouse')
                    }
                    style={{
                      background: colors.white,
                      borderRadius: '8px',
                      padding: '20px',
                      width: '280px',
                      border: warehouse.is_default
                        ? `1px solid ${colors.greenLight}`
                        : `1px solid ${colors.grayBorderLight}`,
                      cursor: 'pointer',
                      transition:
                        'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      ':hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <DcFlexBox flexDirection="column" gap="12px">
                      <DcFlexBox
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <DcText
                          style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: colors.grayTextDark,
                          }}
                        >
                          {warehouse.name}
                        </DcText>
                        {warehouse.is_default && (
                          <DcDiv
                            style={{
                              background: colors.green,
                              color: colors.white,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}
                          >
                            Default
                          </DcDiv>
                        )}
                      </DcFlexBox>

                      <DcDiv
                        style={{ color: colors.grayText, fontSize: '14px' }}
                      >
                        <DcText style={{ marginBottom: '4px' }}>
                          Code: {warehouse.warehouse_code}
                        </DcText>
                        <DcText style={{ marginBottom: '4px' }}>
                          {warehouse.description}
                        </DcText>
                      </DcDiv>

                      <DcDiv
                        style={{
                          borderTop: `1px solid ${colors.grayBorderLight}`,
                          paddingTop: '12px',
                        }}
                      >
                        <DcText
                          style={{
                            color: colors.grayText,
                            fontSize: '14px',
                            marginBottom: '4px',
                          }}
                        >
                          📍 {warehouse.address}
                        </DcText>
                        <DcText
                          style={{
                            color: colors.grayText,
                            fontSize: '14px',
                            marginBottom: '4px',
                          }}
                        >
                          🏙️ {warehouse.city}
                          {warehouse.province && `, ${warehouse.province}`}
                          {warehouse.country && `, ${warehouse.country}`}
                          {warehouse.zip && ` ${warehouse.zip}`}
                        </DcText>
                        <DcText
                          style={{ color: colors.grayText, fontSize: '14px' }}
                        >
                          📞 {warehouse.phone}
                        </DcText>
                      </DcDiv>

                      <DcDiv
                        style={{
                          borderTop: `1px solid ${colors.grayBorderLight}`,
                          paddingTop: '12px',
                        }}
                      >
                        <DcText
                          style={{
                            color: colors.grayTextLight,
                            fontSize: '12px',
                          }}
                        >
                          Created: {formatDate(warehouse.created_at.date)}
                        </DcText>
                      </DcDiv>
                    </DcFlexBox>
                  </DcDiv>
                ))}
              </DcDiv>
            </DcFlexBox>
          </SectionContent>
          &nbsp;
          <SectionContent height="50%" overflow="auto">
            <DcFlexBox flexDirection="column" gap="20px">
              <DcText style={{ fontSize: '18px', fontWeight: 'bold' }}>
                <MarketplaceStallIcon
                  color={colors.grayTextDark}
                  marginBottom="5px"
                  size={20}
                />{' '}
                Marketplace list
              </DcText>
              <DcDiv style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {marketplaces?.map((marketplace) => (
                  <DcDiv
                    key={marketplace.id}
                    hoverStyle={
                      !marketplace.is_default
                        ? { boxShadow: `0px 0px 10px 0px ${colors.grayShadow}` }
                        : { boxShadow: `0px 0px 10px 0px ${colors.green}` }
                    }
                    onClick={() =>
                      handleWarehouseClick(marketplace.wh_id, 'marketplace')
                    }
                    style={{
                      background: colors.white,
                      borderRadius: '8px',
                      padding: '20px',
                      width: '280px',
                      border: marketplace.is_default
                        ? `1px solid ${colors.greenLight}`
                        : `1px solid ${colors.grayBorderLight}`,
                      cursor: 'pointer',
                      transition:
                        'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      ':hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <DcFlexBox flexDirection="column" gap="12px">
                      <DcFlexBox
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <DcText
                          style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: colors.grayTextDark,
                          }}
                        >
                          {marketplace.name}
                        </DcText>
                        {marketplace.is_default && (
                          <DcDiv
                            style={{
                              background: colors.green,
                              color: colors.white,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}
                          >
                            Default
                          </DcDiv>
                        )}
                      </DcFlexBox>

                      <DcDiv
                        style={{ color: colors.grayText, fontSize: '14px' }}
                      >
                        <DcText style={{ marginBottom: '4px' }}>
                          Code: {marketplace.warehouse_code}
                        </DcText>
                        <DcText style={{ marginBottom: '4px' }}>
                          {marketplace.description}
                        </DcText>
                      </DcDiv>

                      <DcDiv
                        style={{
                          borderTop: `1px solid ${colors.grayBorderLight}`,
                          paddingTop: '12px',
                        }}
                      >
                        <DcText
                          style={{
                            color: colors.grayText,
                            fontSize: '14px',
                            marginBottom: '4px',
                          }}
                        >
                          📍 {marketplace.address}
                        </DcText>
                        <DcText
                          style={{
                            color: colors.grayText,
                            fontSize: '14px',
                            marginBottom: '4px',
                          }}
                        >
                          🏙️ {marketplace.city}
                          {marketplace.province && `, ${marketplace.province}`}
                          {marketplace.country && `, ${marketplace.country}`}
                          {marketplace.zip && ` ${marketplace.zip}`}
                        </DcText>
                        <DcText
                          style={{ color: colors.grayText, fontSize: '14px' }}
                        >
                          📞 {marketplace.phone}
                        </DcText>
                      </DcDiv>

                      <DcDiv
                        style={{
                          borderTop: `1px solid ${colors.grayBorderLight}`,
                          paddingTop: '12px',
                        }}
                      >
                        <DcText
                          style={{
                            color: colors.grayTextLight,
                            fontSize: '12px',
                          }}
                        >
                          Created: {formatDate(marketplace.created_at.date)}
                        </DcText>
                      </DcDiv>
                    </DcFlexBox>
                  </DcDiv>
                ))}
              </DcDiv>
            </DcFlexBox>
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </>
  )
}

export default WarehouseView
